import React, { useContext, useEffect, useRef, useState } from 'react'
import { SipContext } from '~/service/SipConn'
import { play, stop, Signal, signalKeys } from '~/utils/sound'
import CallButton from '../common/CallButton'
import MainContent from '../MainContent'

// const defaultVal = '+48796070640'
const defaultVal = '+48'
let initialVal = defaultVal

const normalize = (number: string) =>
  number.replace(/[^0-9\+\*\#]/g, '').replace(/(.)\+/g, '$1')

const Keypad: React.FC = ({}) => {
  const context = useContext(SipContext)
  const [val, setVal] = useState(initialVal)

  const setValRef = useRef(setVal)
  setValRef.current = setVal

  const valRef = useRef(val)
  valRef.current = val

  useEffect(() => {
    return () => {
      initialVal = valRef.current
    }
  }, [])

  const onCallClick = () => {
    const val = valRef.current

    setValRef.current(defaultVal)

    context.makeCall(val)
  }

  useEffect(() => {
    const pressHandler = (e: KeyboardEvent) => {
      e.preventDefault()
    }

    const downHandler = ({ key }: KeyboardEvent) => {
      setVal(
        key == 'Backspace'
          ? valRef.current.slice(0, -1)
          : normalize(valRef.current + key)
      )

      if (key == 'Enter') {
        onCallClick()
      }

      if (signalKeys.includes(key)) {
        play(key as Signal)
        setTimeout(stop, 100)
      }
    }

    window.addEventListener('keypress', pressHandler)
    window.addEventListener('keydown', downHandler)

    return () => {
      window.removeEventListener('keypress', pressHandler)
      window.removeEventListener('keydown', downHandler)
    }
  }, [])

  useEffect(() => {
    function handleEvent(e: ClipboardEvent) {
      if (!e.clipboardData) {
        return
      }

      setValRef.current(normalize(e.clipboardData.getData('Text')))
    }

    document.addEventListener('paste', handleEvent, true)

    return () => {
      document.removeEventListener('paste', handleEvent, true)
    }
  }, [])

  return (
    <MainContent className="is-whiteBg">
      <div className="phone-keypad">
        <div className="phone-keypad-value">{val}</div>

        <div className="phone-keypad-buttons">
          <div>
            <Button val="1" sub={true} setVal={setVal} />
            <Button val="2" sub="ABC" setVal={setVal} />
            <Button val="3" sub="DEF" setVal={setVal} />
          </div>
          <div>
            <Button val="4" sub="GHI" setVal={setVal} />
            <Button val="5" sub="JKL" setVal={setVal} />
            <Button val="6" sub="MNO" setVal={setVal} />
          </div>
          <div>
            <Button val="7" sub="PQRS" setVal={setVal} />
            <Button val="8" sub="TUV" setVal={setVal} />
            <Button val="9" sub="WXYZ" setVal={setVal} />
          </div>
          <div>
            <Button val="*" setVal={setVal} />
            <ButtonZero setVal={setVal} />
            <Button val="#" setVal={setVal} />
          </div>
          <div>
            <div></div>
            <CallButton onClick={onCallClick} />
            <ButtonBackspace setVal={setVal} />
          </div>
        </div>
      </div>
    </MainContent>
  )
}

type ButtonProps = {
  val: Signal
  sub?: string | true
  setVal: React.Dispatch<React.SetStateAction<string>>
}

const Button: React.FC<ButtonProps> = ({ val, sub, setVal }) => {
  return (
    <button
      onMouseDown={() => {
        play(val)
        setVal(v => v + val)
      }}
      onMouseUp={stop}
    >
      {val}
      <div>{sub === true ? <>&nbsp;</> : sub}</div>
    </button>
  )
}

type ButtonBackspaceProps = {
  setVal: React.Dispatch<React.SetStateAction<string>>
}

const ButtonBackspace: React.FC<ButtonBackspaceProps> = ({ setVal }) => {
  return (
    <button
      onMouseDown={() => {
        setVal(v => v.slice(0, -1))
      }}
      className="is-backspace"
    >
      <span className="fal fa-backspace" />
    </button>
  )
}

type ButtonZeroProps = {
  setVal: React.Dispatch<React.SetStateAction<string>>
}

const ButtonZero: React.FC<ButtonZeroProps> = ({ setVal }) => {
  const [down, setDown] = useState(false)
  const downRef = useRef(down)
  downRef.current = down

  return (
    <button
      onMouseDown={() => {
        setVal(v => v + '0')

        setDown(true)

        setTimeout(() => {
          if (downRef.current) {
            setVal(v => (v == '0' ? '+' : v))
          }
        }, 500)

        play('0')
      }}
      onMouseUp={() => {
        setDown(false)
        stop()
      }}
    >
      0<div>+</div>
    </button>
  )
}

export default Keypad
